import {
  uid,
  freeform,
  parseProject,
  distinctProfiles,
  type Field,
  type Project,
} from "@proof/core";

function category(field: Field): string {
  if (field.calculation || field.system) return "Calculated values";
  const text = `${field.key} ${field.label}`
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ");
  if (/address|\bcity\b|\bstate\b|zip|postal|country|directions/i.test(text))
    return "Address";
  if (/phone|fax|cell|mobile|email|website|contact/i.test(text))
    return "Contact information";
  if (
    ["image", "image-list"].includes(field.type) ||
    /photo|logo|artwork|image|qr.?code/i.test(text)
  )
    return "Images & artwork";
  if (/\bname\b|fullname|title|company|organization/i.test(text))
    return "Personal & company details";
  return "Additional information";
}

/** Generate an editable layout locally, preserving existing sections and rule references. */
export function generateFormLayout(project: Project): Project {
  const groups = project.groups.map((g) => ({ ...g }));
  const existing = new Set(groups.map((g) => g.id));
  // With several global profiles, each profile's variables get their own
  // sections so no section mixes profiles; unmapped variables group as usual.
  const profiles = distinctProfiles(project);
  const profileOf = new Map<string, string>();
  if (profiles.length > 1)
    for (const profile of profiles)
      for (const id of profile.fieldIds)
        if (!profileOf.has(id)) profileOf.set(id, profile.name);
  const generated = new Map<string, string>();
  const fields = [...project.fields]
    .sort((a, b) => a.order - b.order)
    .map((field) => {
      if (
        field.formDisplay === "remove" ||
        (field.groupId && existing.has(field.groupId))
      )
        return { ...field };
      const name = profileOf.has(field.id)
        ? `${profileOf.get(field.id)} details`
        : category(field);
      let groupId = generated.get(name);
      if (!groupId) {
        groupId = uid("group");
        generated.set(name, groupId);
        groups.push({
          id: groupId,
          name,
          column: 1,
          order: groups.length,
          visible: name !== "Calculated values",
          note: "",
        });
      }
      return { ...field, groupId };
    });
  const visibleFields = fields.filter(
    (f) => f.formDisplay !== "remove" && !f.calculation && !f.system,
  ).length;
  const columns = visibleFields > 16 ? 3 : visibleFields > 6 ? 2 : 1;
  const loads = Array<number>(columns).fill(0);
  for (const group of groups.sort((a, b) => a.order - b.order)) {
    const column = loads.indexOf(Math.min(...loads));
    group.column = column + 1;
    loads[column] += Math.max(
      1,
      fields.filter((f) => f.groupId === group.id && f.formDisplay !== "remove")
        .length,
    );
  }
  const sequence = groups.flatMap((g) =>
    fields.filter((f) => f.groupId === g.id && f.formDisplay !== "remove"),
  );
  sequence.push(...fields.filter((f) => f.formDisplay === "remove"));
  const order = new Map(sequence.map((f, i) => [f.id, i]));
  const newGroupIds = [...generated.values()];
  return {
    ...project,
    groups,
    fields: project.fields.map((f) => ({
      ...f,
      groupId: fields.find((v) => v.id === f.id)!.groupId,
      order: order.get(f.id) ?? f.order,
    })),
    tabs:
      project.tabs.length && newGroupIds.length
        ? [
            ...project.tabs,
            {
              id: uid("tab"),
              name: "More information",
              order: Math.max(...project.tabs.map((t) => t.order)) + 1,
              groupIds: newGroupIds,
            },
          ]
        : project.tabs,
  };
}

export const formLayoutSignature = (p: Project) =>
  JSON.stringify({
    groups: p.groups,
    tabs: p.tabs,
    fields: p.fields.map((f) => [f.id, f.groupId, f.order, f.formDisplay]),
  });

export function restoreFormLayout(current: Project, before: Project): Project {
  return {
    ...current,
    groups: before.groups,
    tabs: before.tabs,
    fields: current.fields.map((f) => {
      const old = before.fields.find((v) => v.id === f.id);
      return old ? { ...f, groupId: old.groupId, order: old.order } : f;
    }),
  };
}

/** Portable builder state keeps form dependencies while excluding document artwork/output payloads. */
export function formBuilderFromDocument(project: Project): Project {
  return parseProject({
    ...project,
    pages: freeform.pages.map((page) => ({ ...page, nodes: [] })),
    outputs: freeform.outputs,
    resourceSnapshots: project.resourceSnapshots.filter((resource) =>
      ["form", "rule", "preset", "dataset", "validation", "format"].includes(
        resource.kind,
      ),
    ),
    legacy: undefined,
  });
}
